import { LIVE_CLASS_CANCELLATION_WINDOW } from 'config/constants';
import { extractFromSlug } from 'helpers/utils';
import useMobxStores from 'hooks/useMobxStores';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { LiveVirtualClass } from 'types/liveClass';

const resolveCurrentLiveClass = (
  enrolledLiveClass: unknown,
  courseCode: string,
): LiveVirtualClass | null => {
  if (!enrolledLiveClass) return null;

  const liveClass = enrolledLiveClass as Record<string, any>;

  if (
    'offering_id' in liveClass ||
    'start_time' in liveClass ||
    'startTime' in liveClass
  ) {
    return liveClass as unknown as LiveVirtualClass;
  }

  return (liveClass[courseCode] as LiveVirtualClass) ?? null;
};

const useCurrentLiveClass = (courseSlug = '') => {
  const { liveClassStore } = useMobxStores();

  const offeringEnrolledLiveClass = useMemo(
    () =>
      liveClassStore?.offeringEnrolledLiveClass?.length
        ? liveClassStore.offeringEnrolledLiveClass[0]
        : null,
    [liveClassStore?.offeringEnrolledLiveClass],
  );

  const { code: courseCode } = extractFromSlug(courseSlug);

  const canModifyLiveClass = useMemo(() => {
    const current = resolveCurrentLiveClass(
      offeringEnrolledLiveClass,
      courseCode,
    );

    const startTime = current?.start_time;
    const tz = current?.timezone ?? 'UTC';

    if (!startTime) return false;

    const now = DateTime.now().setZone(tz).startOf('day');
    const startDay = DateTime.fromISO(startTime, { zone: tz }).startOf('day');
    const daysUntilStart = startDay.diff(now, 'days').days;

    return daysUntilStart > LIVE_CLASS_CANCELLATION_WINDOW;
  }, [courseCode, offeringEnrolledLiveClass]);

  const currentLiveClassInfo = useMemo(() => {
    const currentLiveClass = resolveCurrentLiveClass(
      offeringEnrolledLiveClass,
      courseCode,
    );

    if (!currentLiveClass) return {};

    const classUUID = currentLiveClass.class_uuid ?? '';
    const startTime = currentLiveClass.start_time;
    const tz = currentLiveClass.timezone ?? 'UTC';

    const startTimeFormatted = DateTime.fromISO(startTime, {
      zone: tz,
    }).toFormat('EEEE, MMM d, yyyy, h a ZZZZ');

    return {
      classUUID,
      hasScheduledLiveClass: Boolean(currentLiveClass.offering_id),
      hasUUID: typeof classUUID === 'string' && classUUID.length > 0,
      offeringId: currentLiveClass.offering_id ?? '',
      offeringName: currentLiveClass.offering_name ?? '',
      startTimeFormatted,
    };
  }, [courseCode, offeringEnrolledLiveClass]);

  return { canModifyLiveClass, currentLiveClassInfo };
};

export default useCurrentLiveClass;
